/**
 * Invitation Service - 邀请服务
 * 提供 Deal 邀请相关 API 调用
 */
import api from './api'

// ============================================
// 类型定义
// ============================================

export type DealRole = 'owner' | 'presales' | 'manager'
export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked'

export interface Invitation {
  id: string
  dealId: string
  dealName: string
  inviterUsername?: string
  inviteeEmail: string
  role: DealRole
  status: InvitationStatus
  expiresAt: string
  createdAt: string
  inviteLink?: string
  daysUntilExpiry: number
}

export interface InvitationPublicInfo {
  valid: boolean
  invitationId?: string
  dealName?: string
  inviteeEmail?: string
  role?: DealRole
  roleDescription?: string
  expiresAt?: string
  reason?: string
  message?: string
}

export interface CreateInvitationRequest {
  dealId: string
  inviteeEmail: string
  role: DealRole
}

export interface AcceptInvitationResponse {
  message: string
  dealId: string
  role: DealRole
}

// ============================================
// 角色显示名称
// ============================================

export const roleDisplayNames: Record<DealRole, string> = {
  owner: '负责人',
  presales: '售前',
  manager: '经理/教练'
}

export const roleDescriptions: Record<DealRole, string> = {
  owner: '完全控制权限',
  presales: '可查看和编辑',
  manager: '仅查看脱敏视图'
}

// ============================================
// API 调用
// ============================================

/**
 * 发送邀请
 */
export async function createInvitation(data: CreateInvitationRequest): Promise<Invitation> {
  const response = await api.post<Invitation>('/invitations', data)
  return response.data
}

/**
 * 通过 token 获取邀请公开信息
 */
export async function getInvitationByToken(token: string): Promise<InvitationPublicInfo> {
  const response = await api.get<InvitationPublicInfo>(`/invitations/token/${token}`)
  return response.data
}

/**
 * 接受邀请
 */
export async function acceptInvitation(invitationId: string): Promise<AcceptInvitationResponse> {
  const response = await api.post<AcceptInvitationResponse>(`/invitations/${invitationId}/accept`)
  return response.data
}

/**
 * 撤销邀请
 */
export async function revokeInvitation(invitationId: string): Promise<void> {
  await api.delete(`/invitations/${invitationId}`)
}

/**
 * 获取 Deal 的邀请列表
 */
export async function getDealInvitations(
  dealId: string, 
  status?: InvitationStatus
): Promise<Invitation[]> {
  const params = status ? { status_filter: status } : {}
  const response = await api.get<Invitation[]>(`/invitations/deal/${dealId}`, { params })
  return response.data
}

export default {
  createInvitation,
  getInvitationByToken,
  acceptInvitation,
  revokeInvitation,
  getDealInvitations,
  roleDisplayNames,
  roleDescriptions
}

